import { PerformanceEmployeePerformanceInsightsSummaryFilter } from 'generated/resources/datatable.graphql'
import {
  AnalyticsPageEmployeeNamesFragment,
  AnalyticsPageLegalEntitiesFragment,
  AnalyticsPageLocationsFragment,
} from 'generated/resources/graphql'

import BarChart from 'modules/core/components/BarChart'
import type { Filter } from 'modules/core/components/DataTable/types'

import { TeamsTeam } from 'modules/performance_analytics/hooks/useGroupByKeysLookupTransformer'

import { useHeadcountChartInteraction } from 'modules/performance_analytics/components/Headcount/hooks/useHeadcountChartInteraction'
import { getColorsBasedOnFilters } from 'modules/performance_analytics/components/Headcount/lib/chartUtils'
import {
  CHART_CONFIG,
  CHART_MARGINS,
} from 'modules/performance_analytics/components/Headcount/lib/constants'
import { truncateText } from 'modules/performance_analytics/components/Headcount/lib/textHelpers'
import type { ChartDataItem } from 'modules/performance_analytics/components/Headcount/lib/types'
import { type GroupByType } from 'modules/performance_analytics/components/helpers'

interface HeadcountChartProps {
  data: ChartDataItem[]
  dataTableFilters: Record<string, unknown>
  enabledFilters: Filter<PerformanceEmployeePerformanceInsightsSummaryFilter>[]
  availableFilters: Filter<PerformanceEmployeePerformanceInsightsSummaryFilter>[]
  onAddFilter: (filter: Filter<PerformanceEmployeePerformanceInsightsSummaryFilter>) => void
  onUpdateFilter: (filter: Filter<PerformanceEmployeePerformanceInsightsSummaryFilter>) => void
  groupBy?: GroupByType
  legalEntities?: AnalyticsPageLegalEntitiesFragment[]
  teams?: Pick<TeamsTeam, 'id' | 'name'>[]
  locations?: AnalyticsPageLocationsFragment[]
  employeesNames?: AnalyticsPageEmployeeNamesFragment[]
}

export const HeadcountChart = ({
  data,
  dataTableFilters,
  enabledFilters,
  availableFilters,
  onAddFilter,
  onUpdateFilter,
  groupBy,
  legalEntities,
  teams,
  locations,
  employeesNames,
}: HeadcountChartProps) => {
  const colors = getColorsBasedOnFilters(
    data,
    dataTableFilters,
    groupBy,
    legalEntities,
    teams,
    locations,
    employeesNames
  )

  const { handleChartClick } = useHeadcountChartInteraction({
    groupBy,
    dataTableFilters,
    enabledFilters,
    availableFilters,
    onAddFilter,
    onUpdateFilter,
    legalEntities,
    teams,
    locations,
    employeesNames,
  })

  return (
    <BarChart
      margin={CHART_MARGINS}
      maxValue={CHART_CONFIG.maxValue}
      keys={CHART_CONFIG.keys}
      indexBy={CHART_CONFIG.indexBy}
      colors={colors}
      colorBy={'indexValue'}
      axisBottom={{
        format: (label: string) => truncateText(label),
        renderTick: ({ x, y, value }) => (
          <g transform={`translate(${x},${y + 12})`}>
            <text
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontFamily: 'Inter',
                fontSize: 9,
              }}
            >
              {truncateText(value)}
            </text>
          </g>
        ),
      }}
      enabledGridY={CHART_CONFIG.enabledGridY}
      data={data}
      onClick={handleChartClick}
      pointer
    />
  )
}
